import {
  CoreFieldMaps,
  AdditionalField,
  Condition,
  Transform,
  TransformMap,
} from './types';
import { addlKey, jqPath, setAtDotted, valAt } from './jqPaths';
import {
  applyTransforms,
  applyTransformsJs,
  unmappedFieldExpr,
} from './transforms';

// ── Field-source resolution (shared by genJq and evalMapping) ──

type ResolvedField = { mapping: string[] | null; chain?: Transform[] };

/** jq expression for a resolved field — a mapped path wrapped in its transforms, or a
 *  null-base chain for a field carried entirely by transforms. */
function resolvedFieldJq(f: ResolvedField): string {
  return f.mapping
    ? applyTransforms(jqPath(f.mapping), f.chain)
    : unmappedFieldExpr(f.chain ?? []);
}

/**
 * Decides which mapping and/or transform chain carries every output field — the single
 * rule shared by genJq (the emitted filter) and evalMapping (the live preview), so the
 * two can never silently diverge. A chain whose emission collapses to bare `null` (e.g.
 * a coalesce pill with no path typed yet) is not usable — emitting `field: null` would
 * persist, then reload as a hardcoded `default "null"` pill.
 */
function resolveFieldSources(
  coreFieldMaps: CoreFieldMaps,
  transforms: TransformMap,
  additionalFields: AdditionalField[],
) {
  const chainUsable = (chain?: Transform[]) =>
    !!chain?.length && unmappedFieldExpr(chain) !== 'null';

  const fields: ({ key: string } & ResolvedField)[] = [];
  for (const [key, mapping] of Object.entries(coreFieldMaps)) {
    if (!mapping) continue;
    if (key === 'location.lat' || key === 'location.lon') continue;
    fields.push({ key, mapping, chain: transforms[key] });
  }
  // Unmapped fields carried entirely by transforms (default/coalesce chains — e.g. a
  // hardcoded event_type, or a subject_type coalesced from a fallback path).
  for (const [key, chain] of Object.entries(transforms)) {
    if (coreFieldMaps[key] || !chainUsable(chain)) continue;
    if (key === 'location.lat' || key === 'location.lon') continue;
    fields.push({ key, mapping: null, chain });
  }

  const axis = (key: 'location.lat' | 'location.lon'): ResolvedField | null => {
    const mapping = coreFieldMaps[key];
    if (mapping) return { mapping, chain: transforms[key] };
    return chainUsable(transforms[key])
      ? { mapping: null, chain: transforms[key] }
      : null;
  };
  const lat = axis('location.lat');
  const lon = axis('location.lon');

  // A named additional field is carried by a mapped path OR a usable transform chain
  // (a path-less field is carried entirely by its transforms — same rule as core fields).
  const additional = additionalFields
    .filter((f) => f.name && (f.path || chainUsable(f.transforms)))
    .map((f) => ({
      name: f.name,
      mapping: f.path ? f.path.split('.') : null,
      chain: f.transforms,
    }));

  return { fields, location: lat || lon ? { lat, lon } : null, additional };
}

// ── jq generation ──

function formatConditionValue(value: string): string | number {
  return isNaN(Number(value)) ? JSON.stringify(value) : Number(value);
}

function conditionToJq(condition: Condition): string | null {
  // Use jqPath so numeric segments are bracketed (`.gws[0].time`), matching
  // the body of the generated mapping. Internal storage stays dotted.
  const fieldPath = jqPath(condition.field.split('.'));
  const formattedValue = formatConditionValue(condition.value);
  const numericValue = Number(condition.value);

  switch (condition.op) {
    case 'not_null':
      return `${fieldPath} != null`;
    case 'is_null':
      return `${fieldPath} == null`;
    case 'equals':
      return `${fieldPath} == ${formattedValue}`;
    case 'not_equals':
      return `${fieldPath} != ${formattedValue}`;
    case 'gt':
      return `${fieldPath} > ${numericValue}`;
    case 'lt':
      return `${fieldPath} < ${numericValue}`;
    case 'contains':
      return `(${fieldPath} | tostring | contains(${JSON.stringify(condition.value || '')}))`;
    default:
      return null;
  }
}

/**
 * Generates a complete jq filter string from the builder state.
 * Produces `{ field: .path, ... }` or `if (conditions) then { ... } else empty end`.
 */
export function genJq(
  conditions: Condition[],
  coreFieldMaps: CoreFieldMaps,
  additionalFields: AdditionalField[],
  objectType = 'obv',
  transforms: TransformMap = {},
): string {
  const conditionClauses = conditions
    .filter((c) => c.field)
    .map(conditionToJq)
    .filter(Boolean) as string[];

  const outputLines: string[] = [];
  const src = resolveFieldSources(coreFieldMaps, transforms, additionalFields);

  // Core fields — mapped, or carried entirely by transforms (location is nested below).
  for (const f of src.fields) {
    outputLines.push(`  ${f.key}: ${resolvedFieldJq(f)}`);
  }

  // Location object (combine lat/lon into nested structure)
  if (src.location) {
    const lat = src.location.lat ? resolvedFieldJq(src.location.lat) : 'null';
    const lon = src.location.lon ? resolvedFieldJq(src.location.lon) : 'null';
    outputLines.push(`  location: { lat: ${lat}, lon: ${lon} }`);
  }

  // Additional / event_details fields
  const additionalLines = src.additional.map(
    (f) => `    ${JSON.stringify(f.name)}: ${resolvedFieldJq(f)}`,
  );
  if (additionalLines.length) {
    outputLines.push(
      `  "${addlKey(objectType)}": {\n${additionalLines.join(',\n')}\n  }`,
    );
  }

  const body = '{\n' + outputLines.join(',\n') + '\n}';
  return conditionClauses.length
    ? `if ( ${conditionClauses.join(' and ')} ) then ${body} else empty end`
    : body;
}

// ── Evaluation (preview) ──

/**
 * Preview value for a mapped field. When the source path is missing, coalesce/default
 * transforms can still rescue the value (that's their whole point) — otherwise show the
 * unmapped placeholder.
 */
function previewValue(
  val: any,
  transforms: Transform[] | undefined,
  input: any,
  placeholder: string,
): any {
  const rescuable = transforms?.some(
    (t) => t.kind === 'coalesce' || t.kind === 'default',
  );
  if (val === undefined && !rescuable) return placeholder;
  const transformed = applyTransformsJs(val, transforms, input);
  return transformed !== undefined ? transformed : placeholder;
}

/**
 * Evaluates the builder mapping against a sample input object and returns the
 * transformed output. Conditions are left out on purpose: the sample is often
 * auto-generated and doesn't match real payloads, so a preview that skipped the
 * record would blame the filter for the sample. The emitted jq (genJq) still
 * applies them.
 */
export function evalMapping(
  input: any,
  coreFieldMaps: CoreFieldMaps,
  additionalFields: AdditionalField[],
  objectType = 'obv',
  complexFields: Record<string, string> = {},
  transforms: TransformMap = {},
): any {
  // Build output object
  const result: any = {};
  const src = resolveFieldSources(coreFieldMaps, transforms, additionalFields);
  // Preview value for a resolved field — the JS mirror of resolvedFieldJq.
  const resolvedFieldValue = (f: {
    mapping: string[] | null;
    chain?: Transform[];
  }): any =>
    f.mapping
      ? previewValue(
          valAt(input, f.mapping),
          f.chain,
          input,
          `<${jqPath(f.mapping)}>`,
        )
      : applyTransformsJs(null, f.chain ?? [], input);

  // Complex fields — include as expression placeholder so validation sees them as mapped.
  // Dotted keys (e.g. "location.lat") must be nested so the validator's getNestedValue resolves them.
  for (const [key, expr] of Object.entries(complexFields)) {
    if (key.startsWith('addl:')) continue; // handled below in additional bag
    setAtDotted(result, key, `<${expr}>`);
  }

  for (const f of src.fields) result[f.key] = resolvedFieldValue(f);

  if (src.location) {
    // Merge with any complex-field placeholders already set via setAtDotted — a mapped lat
    // must not clobber a complex lon (or vice versa).
    const location: any =
      result.location && typeof result.location === 'object'
        ? result.location
        : {};
    if (src.location.lat) location.lat = resolvedFieldValue(src.location.lat);
    else if (location.lat === undefined) location.lat = null;
    if (src.location.lon) location.lon = resolvedFieldValue(src.location.lon);
    else if (location.lon === undefined) location.lon = null;
    result.location = location;
  }

  // Null prototype: a user-named `__proto__` field must land as a key, not swap
  // the bag's prototype and vanish from the preview.
  const additionalBag: any = Object.create(null);
  for (const f of src.additional) additionalBag[f.name] = resolvedFieldValue(f);
  // Include complex additional fields as placeholders
  for (const [key, expr] of Object.entries(complexFields)) {
    if (!key.startsWith('addl:')) continue;
    additionalBag[key.slice(5)] = `<${expr}>`;
  }
  if (Object.keys(additionalBag).length) {
    result[addlKey(objectType)] = additionalBag;
  }

  return result;
}
